import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import { X, Image, Camera, Film, Aperture, Sun, Clapperboard, Video, Play } from 'lucide-react';
import ImageUploadField from './ImageUploadField';
import SocialShareImageField from './SocialShareImageField';

// Logo Upload Box Component - moved outside to prevent re-render issues
const LogoUploadBox = ({ logoType, logoUrl, inputRef, icon: Icon, title, description, bgClass, onRemove, onUploadClick, uploading }) => (
  <div className={`border border-gray-200 rounded-lg p-6 ${bgClass}`}>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={20} className="text-gray-600" />
      <label className="text-sm font-medium">{title}</label>
    </div>
    
    {logoUrl ? (
      <div className="flex items-start gap-4">
        <div className={`relative p-4 rounded-lg border shadow-sm ${logoType === 'film' ? 'bg-gray-900' : 'bg-white'}`}>
          <img 
            src={logoUrl} 
            alt={`${title}`} 
            className="h-16 w-auto object-contain"
            style={logoType === 'film' ? { filter: 'brightness(1.2)' } : {}}
          />
          <button
            onClick={() => onRemove(logoType)}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-2">{description}</p>
          <button
            onClick={() => onUploadClick(inputRef)}
            disabled={uploading}
            className="px-4 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Replace'}
          </button>
        </div>
      </div>
    ) : (
      <div 
        onClick={() => onUploadClick(inputRef)}
        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors bg-white"
      >
        <Image size={32} className="mx-auto text-gray-400 mb-3" />
        <p className="text-sm text-gray-600 mb-1">Click to upload</p>
        <p className="text-xs text-gray-400">SVG recommended</p>
      </div>
    )}
  </div>
);

const SettingsPanel = () => {
  const [settings, setSettings] = useState({
    logo_url: '',
    photo_logo_url: '',
    film_logo_url: '',
    contact_email: '',
    instagram: '',
    location: '',
    tagline: '',
    tagline_description: '',
    cloudfront_url: '',
    description_font: 'playfair',
    blog_visible: true,
    inspo_visible: true,
    landing_photo_icon: 'camera',
    landing_photo_icon_url: '',
    landing_photo_icon_size: 48,
    landing_film_icon: 'film',
    landing_film_icon_url: '',
    landing_film_icon_size: 48,
    google_reviews_url: '',
    favicon_url: '',
    og_image_url: '',
    og_title: '',
    og_description: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ photo: false, film: false });
  const [message, setMessage] = useState('');
  const photoLogoInputRef = useRef(null);
  const filmLogoInputRef = useRef(null);

  // Available fonts for descriptions
  const fontOptions = [
    { value: 'playfair', label: 'Playfair Display', preview: 'font-playfair' },
    { value: 'cormorant', label: 'Cormorant Garamond', preview: 'font-cormorant' },
    { value: 'dm-serif', label: 'DM Serif Display', preview: 'font-dm-serif' },
    { value: 'libre', label: 'Libre Baskerville', preview: 'font-libre' },
    { value: 'crimson', label: 'Crimson Text', preview: 'font-crimson' },
    { value: 'source-serif', label: 'Source Serif Pro', preview: 'font-source-serif' },
  ];

  // Get font family CSS value
  const getFontFamily = (fontValue) => {
    const fontMap = {
      'playfair': '"Playfair Display", serif',
      'cormorant': '"Cormorant Garamond", serif',
      'dm-serif': '"DM Serif Display", serif',
      'libre': '"Libre Baskerville", serif',
      'crimson': '"Crimson Text", serif',
      'source-serif': '"Source Serif Pro", serif',
    };
    return fontMap[fontValue] || fontMap['playfair'];
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.getSettings();
      setSettings({
        logo_url: data.logo_url || '',
        photo_logo_url: data.photo_logo_url || data.logo_url || '',
        film_logo_url: data.film_logo_url || data.logo_url || '',
        contact_email: data.contact_email || '',
        instagram: data.instagram || '',
        location: data.location || '',
        tagline: data.tagline || '',
        tagline_description: data.tagline_description || '',
        cloudfront_url: data.cloudfront_url || '',
        description_font: data.description_font || 'playfair',
        blog_visible: data.blog_visible !== false,
        inspo_visible: data.inspo_visible !== false,
        landing_photo_icon: data.landing_photo_icon || 'camera',
        landing_photo_icon_url: data.landing_photo_icon_url || '',
        landing_photo_icon_size: data.landing_photo_icon_size || 48,
        landing_film_icon: data.landing_film_icon || 'film',
        landing_film_icon_url: data.landing_film_icon_url || '',
        landing_film_icon_size: data.landing_film_icon_size || 48,
        google_reviews_url: data.google_reviews_url || '',
        favicon_url: data.favicon_url || '',
        og_image_url: data.og_image_url || '',
        og_title: data.og_title || '',
        og_description: data.og_description || '',
      });
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const handleLogoUpload = async (e, logoType) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('Please upload an SVG, PNG, JPEG, WebP, or GIF file');
      return;
    }

    setUploading({ ...uploading, [logoType]: true });
    setMessage('');

    try {
      const result = await api.uploadImage(file, 'logos');
      const fieldName = logoType === 'photo' ? 'photo_logo_url' : 'film_logo_url';
      setSettings({ ...settings, [fieldName]: result.url });
      setMessage(`${logoType === 'photo' ? 'Photo' : 'Film'} logo uploaded! Click Save to apply.`);
    } catch (err) {
      setMessage('Failed to upload logo: ' + err.message);
    } finally {
      setUploading({ ...uploading, [logoType]: false });
    }
  };

  const handleRemoveLogo = (logoType) => {
    const fieldName = logoType === 'photo' ? 'photo_logo_url' : 'film_logo_url';
    setSettings({ ...settings, [fieldName]: '' });
    setMessage(`${logoType === 'photo' ? 'Photo' : 'Film'} logo removed. Click Save to apply.`);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      // Also update the legacy logo_url with photo_logo for backwards compatibility
      const settingsToSave = {
        ...settings,
        logo_url: settings.photo_logo_url || settings.logo_url,
      };
      await api.updateSettings(settingsToSave);
      setMessage('Settings updated successfully!');
    } catch (err) {
      setMessage('Failed to update: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadClick = (inputRef) => {
    inputRef.current?.click();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Site Settings</h2>
        <p className="text-sm text-gray-500 mt-1">Configure logos, contact info, and CDN settings</p>
      </div>

      {message && (
        <div className={`p-4 rounded ${message.includes('success') || message.includes('uploaded') || message.includes('removed') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message}
        </div>
      )}

      <div className="space-y-6">
        {/* Logo Section */}
        <div>
          <h3 className="text-lg font-medium mb-4">Logos</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <LogoUploadBox
              logoType="photo"
              logoUrl={settings.photo_logo_url}
              inputRef={photoLogoInputRef}
              icon={Camera}
              title="Photo Section Logo"
              description="Used in the Photo/Gallery section (white background)"
              bgClass="bg-gray-50"
              onRemove={handleRemoveLogo}
              onUploadClick={handleUploadClick}
              uploading={uploading.photo}
            />
            <input
              ref={photoLogoInputRef}
              type="file"
              accept="image/svg+xml,image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => handleLogoUpload(e, 'photo')}
              className="hidden"
            />
            <LogoUploadBox
              logoType="film"
              logoUrl={settings.film_logo_url}
              inputRef={filmLogoInputRef}
              icon={Film}
              title="Film Section Logo"
              description="Used in the Film/Theatre section (dark background with gold accents)"
              bgClass="bg-gradient-to-br from-gray-100 to-gray-200"
              onRemove={handleRemoveLogo}
              onUploadClick={handleUploadClick}
              uploading={uploading.film}
            />
            <input
              ref={filmLogoInputRef}
              type="file"
              accept="image/svg+xml,image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => handleLogoUpload(e, 'film')}
              className="hidden"
            />
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Landing Page Icons */}
        <div>
          <h3 className="text-lg font-medium mb-2">Landing Page Icons</h3>
          <p className="text-xs text-gray-500 mb-4">Upload custom PNG/SVG icons for the homepage Photo & Film selector. If no image is uploaded, a default icon is used.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Photo Icon */}
            <div className="space-y-3">
              <label className="block text-sm font-medium">Photo Icon</label>
              <ImageUploadField
                value={settings.landing_photo_icon_url || ''}
                onChange={(url) => setSettings({ ...settings, landing_photo_icon_url: url })}
                folder="icons"
                label="Upload Photo Icon (PNG/SVG)"
              />
              <div>
                <label className="block text-xs text-gray-500 mb-1">Icon Size: {settings.landing_photo_icon_size || 48}px</label>
                <input
                  type="range"
                  min={24}
                  max={120}
                  step={4}
                  value={settings.landing_photo_icon_size || 48}
                  onChange={(e) => setSettings({ ...settings, landing_photo_icon_size: parseInt(e.target.value) })}
                  className="w-full accent-black"
                  data-testid="photo-icon-size-slider"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5"><span>24px</span><span>120px</span></div>
              </div>
              {settings.landing_photo_icon_url && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <img src={settings.landing_photo_icon_url} alt="Photo icon preview" style={{ width: settings.landing_photo_icon_size || 48, height: settings.landing_photo_icon_size || 48 }} className="object-contain" />
                  <span className="text-xs text-gray-500">Preview at {settings.landing_photo_icon_size || 48}px</span>
                </div>
              )}
            </div>
            {/* Film Icon */}
            <div className="space-y-3">
              <label className="block text-sm font-medium">Film Icon</label>
              <ImageUploadField
                value={settings.landing_film_icon_url || ''}
                onChange={(url) => setSettings({ ...settings, landing_film_icon_url: url })}
                folder="icons"
                label="Upload Film Icon (PNG/SVG)"
              />
              <div>
                <label className="block text-xs text-gray-500 mb-1">Icon Size: {settings.landing_film_icon_size || 48}px</label>
                <input
                  type="range"
                  min={24}
                  max={120}
                  step={4}
                  value={settings.landing_film_icon_size || 48}
                  onChange={(e) => setSettings({ ...settings, landing_film_icon_size: parseInt(e.target.value) })}
                  className="w-full accent-black"
                  data-testid="film-icon-size-slider"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5"><span>24px</span><span>120px</span></div>
              </div>
              {settings.landing_film_icon_url && (
                <div className="flex items-center gap-3 p-3 bg-gray-900 rounded-lg">
                  <img src={settings.landing_film_icon_url} alt="Film icon preview" style={{ width: settings.landing_film_icon_size || 48, height: settings.landing_film_icon_size || 48 }} className="object-contain" />
                  <span className="text-xs text-gray-400">Preview at {settings.landing_film_icon_size || 48}px</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Google Reviews URL */}
        <div>
          <h3 className="text-lg font-medium mb-2">Google Reviews Link</h3>
          <p className="text-xs text-gray-500 mb-3">Link to your Google Business reviews page (shown on About page under testimonials)</p>
          <input
            type="url"
            value={settings.google_reviews_url || ''}
            onChange={(e) => setSettings({ ...settings, google_reviews_url: e.target.value })}
            className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-black focus:outline-none"
            placeholder="https://g.page/r/your-business/review"
            data-testid="google-reviews-url-input"
          />
        </div>

        <hr className="border-gray-200" />

        {/* Favicon & Social Sharing */}
        <div>
          <h3 className="text-lg font-medium mb-2">Favicon & Social Sharing</h3>
          <p className="text-xs text-gray-500 mb-4">Customize the browser tab icon (favicon) and how your site appears when shared on social media</p>
          
          <div className="space-y-5">
            {/* Favicon */}
            <div>
              <label className="block text-sm font-medium mb-1">Favicon</label>
              <p className="text-xs text-gray-400 mb-2">Upload a square image (PNG, ICO, or SVG) for the browser tab icon</p>
              <ImageUploadField
                value={settings.favicon_url || ''}
                onChange={(url) => setSettings({ ...settings, favicon_url: url })}
                folder="branding"
                label="Upload Favicon"
              />
              {settings.favicon_url && (
                <div className="flex items-center gap-3 mt-2 p-3 bg-gray-50 rounded-lg">
                  <img src={settings.favicon_url} alt="Favicon preview" className="w-8 h-8 object-contain" />
                  <span className="text-xs text-gray-500">Favicon preview</span>
                </div>
              )}
            </div>

            {/* OG Image */}
            <div>
              <label className="block text-sm font-medium mb-1">Social Share Image</label>
              <p className="text-xs text-gray-400 mb-3">Shown when any page of your site is shared on Instagram, WhatsApp, iMessage, Facebook, LinkedIn — and used as the site image in Google results.</p>
              <SocialShareImageField
                value={settings.og_image_url || ''}
                onChange={(url) => setSettings({ ...settings, og_image_url: url })}
                title={settings.og_title}
                description={settings.og_description}
              />
            </div>

            {/* OG Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Social Share Title</label>
              <p className="text-xs text-gray-400 mb-2">Override the default title shown when sharing links (leave empty to use page titles)</p>
              <input
                type="text"
                value={settings.og_title || ''}
                onChange={(e) => setSettings({ ...settings, og_title: e.target.value })}
                className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-black focus:outline-none"
                placeholder="e.g., Crystal Media — Luxury Wedding Photography & Films"
                data-testid="og-title-input"
              />
            </div>

            {/* OG Description */}
            <div>
              <label className="block text-sm font-medium mb-1">Social Share Description</label>
              <p className="text-xs text-gray-400 mb-2">Override the default description shown when sharing links</p>
              <textarea
                value={settings.og_description || ''}
                onChange={(e) => setSettings({ ...settings, og_description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-black focus:outline-none"
                placeholder="A brief description of your business for social sharing..."
                data-testid="og-description-input"
              />
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* CDN Settings */}
        <div>
          <h3 className="text-lg font-medium mb-4">CDN Settings (Optional)</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>CloudFront CDN</strong> speeds up image and video delivery by serving content from edge locations closest to your users.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">CloudFront Distribution URL</label>
            <input
              type="url"
              value={settings.cloudfront_url}
              onChange={(e) => setSettings({ ...settings, cloudfront_url: e.target.value })}
              placeholder="https://d1234abcd.cloudfront.net"
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to use direct S3 URLs. Once set, new uploads will use this CDN for faster delivery.
            </p>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Contact Settings */}
        <div>
          <h3 className="text-lg font-medium mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Contact Email</label>
              <input
                type="email"
                value={settings.contact_email}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Instagram Handle</label>
              <input
                type="text"
                value={settings.instagram}
                onChange={(e) => setSettings({ ...settings, instagram: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Location</label>
              <input
                type="text"
                value={settings.location}
                onChange={(e) => setSettings({ ...settings, location: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Tagline Settings */}
        <div>
          <h3 className="text-lg font-medium mb-4">Tagline</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Main Tagline</label>
              <input
                type="text"
                value={settings.tagline}
                onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Tagline Description</label>
              <textarea
                value={settings.tagline_description}
                onChange={(e) => setSettings({ ...settings, tagline_description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Blog Visibility */}
        <div>
          <h3 className="text-lg font-medium mb-4">Page Visibility</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Blog Page</p>
                <p className="text-xs text-gray-500">Show or hide the Blog link in the site navigation</p>
              </div>
              <button
                data-testid="blog-visibility-toggle"
                onClick={() => setSettings({ ...settings, blog_visible: !settings.blog_visible })}
                className={`relative w-11 h-6 rounded-full transition-colors ${settings.blog_visible ? 'bg-black' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.blog_visible ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Inspo Page</p>
                <p className="text-xs text-gray-500">Show or hide the Inspo (favorites) link in the site navigation</p>
              </div>
              <button
                data-testid="inspo-visibility-toggle"
                onClick={() => setSettings({ ...settings, inspo_visible: !settings.inspo_visible })}
                className={`relative w-11 h-6 rounded-full transition-colors ${settings.inspo_visible ? 'bg-black' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.inspo_visible ? 'translate-x-5' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Typography Settings */}
        <div>
          <h3 className="text-lg font-medium mb-4">Typography</h3>
          <div>
            <label className="block text-sm font-medium mb-2">Description Font</label>
            <p className="text-xs text-gray-500 mb-3">Choose the font for descriptions and body text across the site</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {fontOptions.map((font) => (
                <button
                  key={font.value}
                  type="button"
                  onClick={() => setSettings({ ...settings, description_font: font.value })}
                  className={`p-4 border rounded-lg text-left transition-all ${
                    settings.description_font === font.value
                      ? 'border-black bg-black/5 ring-1 ring-black'
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <p className="text-sm font-medium mb-1">{font.label}</p>
                  <p 
                    className={`text-lg text-gray-600 italic ${font.preview}`}
                    style={{ fontFamily: getFontFamily(font.value) }}
                  >
                    The quick brown fox
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:bg-gray-400"
          >
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
